import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, Loader2, Maximize2, Printer, ZoomIn, ZoomOut } from 'lucide-react';
import { toast } from 'sonner';
import { downloadInvoiceFile, printInvoicePages } from './invoiceFile';

const MIN_ZOOM = 0.3;
const MAX_ZOOM = 4;
const ZOOM_STEP = 1.25;
const GUTTER = 24; // px breathing room so a fit-width page isn't flush to the edges

const clampZoom = (z) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));

/**
 * Invoice viewer — ALL pages stacked in one vertical scroll (server-rendered
 * PDF→PNG page images). Zoom scales every page's width; the container scrolls
 * both axes. Prev/Next jump between pages and the indicator tracks the page
 * nearest the viewport centre. Blob URLs are revoked on unmount.
 */
export function InvoiceViewer({ api, itemId, pageCount = 1, filename = 'invoice', base = '/cost-inbox' }) {
  const n = Math.max(1, pageCount);
  const [pages, setPages] = useState([]);        // index → blob URL
  const [zoom, setZoom] = useState(1);           // 1 = fit container width
  const [boxW, setBoxW] = useState(0);
  const [current, setCurrent] = useState(0);
  const [loadedCount, setLoadedCount] = useState(0);
  const [error, setError] = useState(null);
  const [fileBusy, setFileBusy] = useState(null); // 'print' | 'download'
  const scrollRef = useRef(null);
  const pageEls = useRef([]);
  const zoomRef = useRef(1);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);

  // Two-finger pinch zooms the document (native listener so preventDefault stops the browser zooming the page).
  useEffect(() => {
    const box = scrollRef.current;
    if (!box) return undefined;
    let start = null;
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const onStart = (e) => { if (e.touches.length === 2) start = { d: dist(e.touches), z: zoomRef.current }; };
    const onMove = (e) => { if (start && e.touches.length === 2) { e.preventDefault(); setZoom(clampZoom(start.z * dist(e.touches) / start.d)); } };
    const onEnd = () => { start = null; };
    box.addEventListener('touchstart', onStart, { passive: true });
    box.addEventListener('touchmove', onMove, { passive: false });
    box.addEventListener('touchend', onEnd);
    box.addEventListener('touchcancel', onEnd);
    return () => { box.removeEventListener('touchstart', onStart); box.removeEventListener('touchmove', onMove); box.removeEventListener('touchend', onEnd); box.removeEventListener('touchcancel', onEnd); };
  }, []);

  const runFile = async (kind) => {
    setFileBusy(kind);
    try {
      if (kind === 'download') await downloadInvoiceFile(api, itemId, filename, base);
      else await printInvoicePages(api, itemId, n, filename, base);
    } catch { toast.error(kind === 'download' ? 'Could not download the file' : 'Could not prepare the print'); }
    finally { setFileBusy(null); }
  };

  // Fetch every page up front (invoices are small); show each as it arrives.
  useEffect(() => {
    let alive = true;
    const urls = [];
    setPages([]); setLoadedCount(0); setError(null); setCurrent(0);
    pageEls.current = [];
    (async () => {
      for (let i = 0; i < n; i++) {
        try {
          const r = await api.get(`${base}/${itemId}/pages/${i}?width=1600`, { responseType: 'blob' });
          if (!alive) return;
          const u = URL.createObjectURL(r.data);
          urls.push(u);
          setPages(prev => { const copy = [...prev]; copy[i] = u; return copy; });
          setLoadedCount(c => c + 1);
        } catch {
          if (!alive) return;
          if (i === 0) setError('Could not load this document');
        }
      }
    })();
    return () => { alive = false; urls.forEach(u => URL.revokeObjectURL(u)); };
  }, [api, itemId, n, base]);

  // Track container width for fit-to-width zoom.
  useLayoutEffect(() => {
    const box = scrollRef.current;
    if (!box || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setBoxW(box.clientWidth));
    ro.observe(box);
    setBoxW(box.clientWidth);
    return () => ro.disconnect();
  }, []);

  const pageWidth = Math.max(80, (boxW - GUTTER) * zoom);

  const scrollToPage = useCallback((i) => {
    const idx = Math.max(0, Math.min(n - 1, i));
    const el = pageEls.current[idx];
    const box = scrollRef.current;
    if (el && box) box.scrollTo({ top: el.offsetTop - 12, behavior: 'smooth' });
    setCurrent(idx);
  }, [n]);

  const onScroll = () => {
    const box = scrollRef.current;
    if (!box) return;
    const mid = box.scrollTop + box.clientHeight / 2;
    let idx = 0;
    for (let i = 0; i < pageEls.current.length; i++) {
      const el = pageEls.current[i];
      if (el && el.offsetTop <= mid) idx = i;
    }
    setCurrent(idx);
  };

  const zoomBy = (factor) => setZoom(z => clampZoom(z * factor));

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="invoice-viewer">
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-white/10 bg-[#1E2530] text-xs text-gray-300 whitespace-nowrap overflow-x-auto shrink-0">
        <button type="button" disabled={current === 0} onClick={() => scrollToPage(current - 1)} className="p-1 rounded hover:bg-white/10 disabled:opacity-30 shrink-0" data-testid="viewer-prev-page" title="Previous page"><ChevronLeft className="w-4 h-4" /></button>
        <span className="tabular-nums shrink-0" data-testid="viewer-page-indicator">{current + 1} / {n}</span>
        <button type="button" disabled={current >= n - 1} onClick={() => scrollToPage(current + 1)} className="p-1 rounded hover:bg-white/10 disabled:opacity-30 shrink-0" data-testid="viewer-next-page" title="Next page"><ChevronRight className="w-4 h-4" /></button>
        <span className="mx-2 h-4 w-px bg-white/10 shrink-0" />
        <button type="button" onClick={() => zoomBy(1 / ZOOM_STEP)} className="p-1 rounded hover:bg-white/10 shrink-0" data-testid="viewer-zoom-out" title="Zoom out"><ZoomOut className="w-4 h-4" /></button>
        <span className="tabular-nums w-12 text-center shrink-0" data-testid="viewer-zoom-level">{Math.round(zoom * 100)}%</span>
        <button type="button" onClick={() => zoomBy(ZOOM_STEP)} className="p-1 rounded hover:bg-white/10 shrink-0" data-testid="viewer-zoom-in" title="Zoom in"><ZoomIn className="w-4 h-4" /></button>
        <button type="button" onClick={() => setZoom(1)} className="ml-1 px-2 py-0.5 rounded hover:bg-white/10 inline-flex items-center gap-1 shrink-0" data-testid="viewer-fit" title="Fit width"><Maximize2 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Fit</span></button>
        <span className="mx-2 h-4 w-px bg-white/10 shrink-0" />
        <button type="button" disabled={!!fileBusy} onClick={() => runFile('print')} className="px-2 py-0.5 rounded hover:bg-white/10 inline-flex items-center gap-1 disabled:opacity-50 shrink-0" data-testid="viewer-print" title="Print invoice">
          {fileBusy === 'print' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />} <span className="hidden sm:inline">Print</span>
        </button>
        <button type="button" disabled={!!fileBusy} onClick={() => runFile('download')} className="px-2 py-0.5 rounded hover:bg-white/10 inline-flex items-center gap-1 disabled:opacity-50 shrink-0" data-testid="viewer-download" title="Download original file">
          {fileBusy === 'download' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} <span className="hidden sm:inline">Download</span>
        </button>
        {n > 1 && <span className="ml-auto text-gray-500 hidden lg:inline" data-testid="viewer-multipage-hint">{n} pages · scroll to read all</span>}
      </div>
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="relative flex-1 min-h-0 overflow-auto bg-[#0F141B]"
        style={{ touchAction: 'pan-x pan-y' }}
        data-testid="viewer-canvas"
      >
        {error && <div className="absolute inset-0 flex items-center justify-center text-sm text-red-300">{error}</div>}
        {!error && (
          <div className="w-max min-w-full flex flex-col items-center gap-3 py-3">
            {Array.from({ length: n }).map((_, i) => (
              <div
                key={i}
                ref={el => { pageEls.current[i] = el; }}
                className="relative shadow-2xl bg-white shrink-0"
                style={{ width: pageWidth }}
                onDoubleClick={() => setZoom(z => (z === 1 ? 2 : 1))}
                data-testid={`viewer-page-${i}`}
              >
                {pages[i] ? (
                  <img src={pages[i]} alt={`Invoice page ${i + 1}`} draggable={false} className="block w-full h-auto select-none" data-testid={`viewer-page-image-${i}`} />
                ) : (
                  <div className="flex items-center justify-center bg-[#151B24]" style={{ height: pageWidth * 1.3 }}>
                    <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
                  </div>
                )}
                {n > 1 && <span className="absolute top-1.5 left-1.5 text-[10px] font-medium bg-black/55 text-white px-1.5 py-0.5 rounded" data-testid={`viewer-page-badge-${i}`}>{i + 1}</span>}
              </div>
            ))}
          </div>
        )}
        {loadedCount < n && !error && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[11px] text-gray-300 bg-black/60 rounded-full px-3 py-1 inline-flex items-center gap-1.5" data-testid="viewer-loading-more">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading pages {loadedCount}/{n}
          </div>
        )}
      </div>
    </div>
  );
}
